import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-toastify';
import authApi from '../api/authApi';
import settingsApi from '../api/settingsApi';
import { AUTH_NOTICE_KEY } from '../utils/nodeApiClient';
import AuthLayout from './AuthLayout';
import { Eye, EyeOff, User, Lock, ShieldCheck, RefreshCw, ArrowRight } from 'lucide-react';

const Login = () => {
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    captcha: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const { login } = useAuth();
  const navigate = useNavigate();

  const [captchaId, setCaptchaId] = useState(null);
  const [captchaImage, setCaptchaImage] = useState(null);
  const [captchaLoading, setCaptchaLoading] = useState(false);

  // Guards against a captcha request resolving after the component has
  // already unmounted (e.g. the user navigates away mid-request), which
  // would otherwise fire a spurious "Failed to load captcha" toast.
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const loadCaptcha = useCallback(async () => {
    setCaptchaLoading(true);
    try {
      const response = await authApi.getCaptcha();
      if (!isMountedRef.current) return;
      if (response.data.success) {
        setCaptchaId(response.data.captcha_id);
        setCaptchaImage(response.data.image);
        setFormData(prev => ({ ...prev, captcha: '' }));
      } else {
        toast.error('Failed to load captcha');
      }
    } catch (error) {
      if (isMountedRef.current) {
        toast.error('Failed to load captcha');
      }
    } finally {
      if (isMountedRef.current) {
        setCaptchaLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    loadCaptcha();
  }, [loadCaptcha]);

  // "Forgot Password?" follows the admin setting. Hidden until known (and on
  // error); the forgot-password API enforces the setting regardless.
  const [forgotPasswordEnabled, setForgotPasswordEnabled] = useState(false);
  useEffect(() => {
    settingsApi.getForgotPassword()
      .then((response) => {
        if (isMountedRef.current) setForgotPasswordEnabled(Boolean(response.data?.data?.enabled));
      })
      .catch(() => {});
  }, []);

  // Set by nodeApiClient when a password change revoked this session.
  useEffect(() => {
    try {
      const notice = sessionStorage.getItem(AUTH_NOTICE_KEY);
      if (notice) {
        sessionStorage.removeItem(AUTH_NOTICE_KEY);
        toast.info(notice);
      }
    } catch (e) {
      // storage unavailable
    }
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    if (name === 'password') {
      setTouched(prev => ({ ...prev, password: true }));
      const newErrors = { ...errors };
      if (!value) {
        if (touched.password) newErrors.password = 'Password is required';
      } else {
        delete newErrors.password;
      }
      setErrors(newErrors);
    }

    if (name === 'captcha') {
      setTouched(prev => ({ ...prev, captcha: true }));
      const newErrors = { ...errors };
      if (!value.trim() && touched.captcha) {
        newErrors.captcha = 'Captcha is required';
      } else {
        delete newErrors.captcha;
      }
      setErrors(newErrors);
    }
  };

  const handleUsernameChange = (e) => {
    const { value } = e.target;
    setFormData(prev => ({ ...prev, username: value }));
    setTouched(prev => ({ ...prev, username: true }));

    const newErrors = { ...errors };
    if (!value.trim()) {
      if (touched.username) newErrors.username = 'Username or email is required';
    } else if (value.trim().length < 3) {
      newErrors.username = 'Username must be at least 3 characters';
    } else if (isEmailFormat(value.trim()) && !isValidEmail(value.trim())) {
      newErrors.username = 'Please enter a valid email address';
    } else {
      delete newErrors.username;
    }
    setErrors(newErrors);
  };

  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const isEmailFormat = (input) => input.includes('@') && input.includes('.');

  const validateForm = () => {
    const newErrors = {};

    if (!formData.username.trim()) {
      newErrors.username = 'Username or email is required';
    } else if (formData.username.trim().length < 3) {
      newErrors.username = 'Username must be at least 3 characters';
    } else if (isEmailFormat(formData.username.trim()) && !isValidEmail(formData.username.trim())) {
      newErrors.username = 'Please enter a valid email address';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    }

    if (!formData.captcha.trim()) {
      newErrors.captcha = 'Captcha is required';
    }

    setErrors(newErrors);
    setTouched({ username: true, password: true, captcha: true });
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Please fix the errors below');
      return;
    }

    if (!captchaId) {
      toast.error('Captcha not loaded yet, please wait');
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      const result = await login(formData.username, formData.password, captchaId, formData.captcha);

      if (result.success && result.twoFactorRequired) {
        toast.info(result.message || 'A verification code has been sent to your email.');
        navigate('/verify-otp');
      } else if (result.success) {
        toast.success('Login successful!');
        setFormData({ username: '', password: '', captcha: '' });
      } else {
        toast.error(result.message || 'Login failed');
        // The captcha (correct or not) is single-use server-side, so a fresh
        // one is always needed after any failed attempt.
        loadCaptcha();
      }
    } catch (error) {
      toast.error('An error occurred. Please try again.');
      loadCaptcha();
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout>
      <style>{`
        .login-card {
          position: relative;
          z-index: 2;
          background: white;
          border-radius: 1.25rem;
          box-shadow: 0 25px 60px -15px rgba(22, 66, 176, 0.55);
          padding: 2.25rem 2rem;
          width: 100%;
          overflow: hidden;
          animation: login-card-in 0.5s cubic-bezier(0.16, 1, 0.3, 1) both;
        }
        /* Slim shimmering accent seam along the top of the card. */
        .login-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 4px;
          background: linear-gradient(90deg, #60a5fa, #1642b0, #60a5fa);
          background-size: 200% auto;
          animation: login-shine 6s ease-in-out infinite;
        }
        @keyframes login-card-in {
          from { opacity: 0; transform: translateY(14px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes login-shine {
          0%, 100% { background-position: 0% center; }
          50% { background-position: 100% center; }
        }
        @media (prefers-reduced-motion: reduce) {
          .login-card { animation: none; }
          .login-card::before { animation: none; }
        }

        /* Icon in a field's leading position brightens while that field has focus. */
        .login-field-wrap svg.login-field-icon {
          color: #9ca3af;
          transition: color 0.15s ease;
        }
        .login-field-wrap:focus-within svg.login-field-icon {
          color: #3b82f6;
        }

        /* Shared form control styling: base state lives here so :focus can
           add a ring (inline styles can't express pseudo-classes). Error /
           default border and background stay inline since they're dynamic. */
        .login-input {
          transition: border-color 0.15s ease, box-shadow 0.15s ease, background-color 0.15s ease;
        }
        .login-input:hover {
          border-color: #94a3b8;
        }
        .login-input:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.18);
        }
        .login-input-error:focus {
          border-color: #dc2626;
          box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.15);
        }

        .login-submit-btn {
          position: relative;
          overflow: hidden;
          transition: transform 0.15s ease, box-shadow 0.15s ease, filter 0.15s ease;
          box-shadow: 0 10px 25px -8px rgba(29, 78, 216, 0.55);
        }
        .login-submit-btn:hover:not(:disabled) {
          filter: brightness(1.06);
          box-shadow: 0 14px 30px -8px rgba(29, 78, 216, 0.65);
        }
        .login-submit-btn:active:not(:disabled) {
          transform: scale(0.98);
        }
        .login-submit-btn::after {
          content: '';
          position: absolute;
          top: 0;
          left: -60%;
          width: 35%;
          height: 100%;
          background: linear-gradient(120deg, transparent, rgba(255,255,255,0.4), transparent);
          transform: skewX(-20deg);
          transition: left 0.6s ease;
        }
        .login-submit-btn:hover:not(:disabled)::after {
          left: 130%;
        }

        .login-forgot-link {
          font-size: 0.875rem;
          font-weight: 600;
          color: #2563eb;
          text-decoration: none;
        }
        .login-forgot-link:hover {
          text-decoration: underline;
        }

        .login-captcha-field {
          flex: 1;
        }
        .login-captcha-image {
          width: 165px;
          flex-shrink: 0;
          transform: rotate(-1.5deg);
        }
        .login-captcha-refresh {
          width: 42px;
          flex-shrink: 0;
        }
        .captcha-refresh-btn {
          transition: transform 0.2s ease;
        }
        .captcha-refresh-btn:hover:not(:disabled) {
          transform: rotate(90deg);
        }
        .captcha-refresh-btn.spinning svg {
          animation: captcha-spin 0.7s linear infinite;
        }
        @keyframes captcha-spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @media (max-width: 480px) {
          .login-card { padding: 1.5rem 1.25rem; }

          /* Show the captcha image (+ refresh) first, then the text field
             on its own full-width row below it, matching the username/
             password fields instead of squeezing it in alongside them. */
          .login-captcha-row {
            flex-wrap: wrap;
          }
          .login-captcha-image {
            order: 1;
            flex: 1 1 auto;
            width: auto;
            height: 46px;
          }
          .login-captcha-refresh {
            order: 2;
          }
          .login-captcha-field {
            order: 3;
            flex: 1 1 100%;
          }
        }
      `}</style>

        {/* Login card */}
        <div className="login-card">
          <div style={{ textAlign: 'center', marginBottom: '1.75rem' }}>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1rem' }}>
              <img src="/wi-logo.svg" alt="Webindia - complete business solutions" style={{ height: '42px', width: 'auto' }} />
            </div>
            <h2 style={{ fontSize: '1.5rem', fontWeight: 800, color: '#0f172a', margin: '0 0 0.35rem' }}>Welcome Back</h2>
            <p style={{ color: '#6b7280', fontSize: '0.9rem', margin: 0 }}>Sign in to your CRM account</p>
          </div>

          <form onSubmit={handleSubmit} noValidate>
            {/* Username */}
            <div style={{ marginBottom: '1.25rem' }}>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem' }}>
                Username or Email<span style={{ color: '#dc2626', marginLeft: '0.25rem' }}>*</span>
              </label>
              <div className="login-field-wrap" style={{ position: 'relative' }}>
                <User className="login-field-icon" style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', width: '1.1rem', height: '1.1rem' }} />
                <input
                  type="text"
                  name="username"
                  value={formData.username}
                  maxLength={100}
                  onChange={handleUsernameChange}
                  placeholder="Enter username or email"
                  required
                  className={`login-input${errors.username ? ' login-input-error' : ''}`}
                  style={{
                    width: '100%',
                    padding: '0.7rem 0.75rem 0.7rem 2.4rem',
                    border: `1px solid ${errors.username ? '#dc2626' : '#d1d5db'}`,
                    borderRadius: '0.6rem',
                    fontSize: '0.95rem',
                    backgroundColor: errors.username ? '#fef2f2' : 'white'
                  }}
                  onBlur={() => {
                    setTouched(prev => ({ ...prev, username: true }));
                    if (!formData.username.trim()) {
                      setErrors(prev => ({ ...prev, username: 'Username or email is required' }));
                    }
                  }}
                />
              </div>
              {errors.username && <div style={{ marginTop: '0.3rem', fontSize: '0.75rem', color: '#dc2626' }}>{errors.username}</div>}
            </div>

            {/* Password */}
            <div style={{ marginBottom: '1.25rem' }}>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem' }}>
                Password<span style={{ color: '#dc2626', marginLeft: '0.25rem' }}>*</span>
              </label>
              <div className="login-field-wrap" style={{ position: 'relative' }}>
                <Lock className="login-field-icon" style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', width: '1.1rem', height: '1.1rem' }} />
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  maxLength={255}
                  onChange={handleChange}
                  placeholder="Enter your password"
                  required
                  className={`login-input${errors.password ? ' login-input-error' : ''}`}
                  style={{
                    width: '100%',
                    padding: '0.7rem 2.4rem 0.7rem 2.4rem',
                    border: `1px solid ${errors.password ? '#dc2626' : '#d1d5db'}`,
                    borderRadius: '0.6rem',
                    fontSize: '0.95rem',
                    backgroundColor: errors.password ? '#fef2f2' : 'white'
                  }}
                  onBlur={() => {
                    setTouched(prev => ({ ...prev, password: true }));
                    if (!formData.password) {
                      setErrors(prev => ({ ...prev, password: 'Password is required' }));
                    }
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  style={{ position: 'absolute', right: '0.75rem', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', display: 'flex' }}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {errors.password && <div style={{ marginTop: '0.3rem', fontSize: '0.75rem', color: '#dc2626' }}>{errors.password}</div>}
            </div>

            {/* Captcha */}
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem' }}>
                Enter Captcha<span style={{ color: '#dc2626', marginLeft: '0.25rem' }}>*</span>
              </label>
              <div className="login-captcha-row" style={{ display: 'flex', gap: '0.6rem', alignItems: 'stretch' }}>
                <div className="login-captcha-field login-field-wrap" style={{ position: 'relative' }}>
                  <ShieldCheck className="login-field-icon" style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', width: '1.1rem', height: '1.1rem' }} />
                  <input
                    type="text"
                    name="captcha"
                    value={formData.captcha}
                    onChange={handleChange}
                    placeholder="Enter the code shown"
                    autoComplete="off"
                    required
                    className={`login-input${errors.captcha ? ' login-input-error' : ''}`}
                    style={{
                      width: '100%',
                      height: '100%',
                      padding: '0.7rem 0.75rem 0.7rem 2.4rem',
                      border: `1px solid ${errors.captcha ? '#dc2626' : '#d1d5db'}`,
                      borderRadius: '0.6rem',
                      fontSize: '0.95rem',
                      backgroundColor: errors.captcha ? '#fef2f2' : 'white'
                    }}
                    onBlur={() => {
                      setTouched(prev => ({ ...prev, captcha: true }));
                      if (!formData.captcha.trim()) {
                        setErrors(prev => ({ ...prev, captcha: 'Captcha is required' }));
                      }
                    }}
                  />
                </div>

                <div className="login-captcha-image" style={{
                  borderRadius: '0.6rem',
                  overflow: 'hidden',
                  border: '1px solid #d1d5db',
                  backgroundColor: '#eef2ff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  {captchaImage ? (
                    <img src={captchaImage} alt="Captcha" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                  ) : (
                    <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>Loading…</span>
                  )}
                </div>

                <button
                  type="button"
                  onClick={loadCaptcha}
                  disabled={captchaLoading}
                  aria-label="Refresh captcha"
                  title="Refresh captcha"
                  className={`captcha-refresh-btn login-captcha-refresh${captchaLoading ? ' spinning' : ''}`}
                  style={{
                    border: '1px solid #d1d5db',
                    borderRadius: '0.6rem',
                    backgroundColor: 'white',
                    color: '#3b82f6',
                    cursor: captchaLoading ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <RefreshCw size={18} />
                </button>
              </div>
              {errors.captcha && <div style={{ marginTop: '0.3rem', fontSize: '0.75rem', color: '#dc2626' }}>{errors.captcha}</div>}
            </div>

            <button
              type="submit"
              disabled={loading}
              className="login-submit-btn"
              style={{
                width: '100%',
                padding: '0.8rem',
                background: loading ? '#9ca3af' : 'linear-gradient(90deg, #1642b0, #4f8ef7)',
                color: 'white',
                border: 'none',
                borderRadius: '0.6rem',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: loading ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem'
              }}
            >
              {loading ? 'Signing in...' : (<>Sign In <ArrowRight size={18} /></>)}
            </button>

            {forgotPasswordEnabled && (
              <div style={{ textAlign: 'center', marginTop: '1.1rem' }}>
                <Link to="/forgot-password" className="login-forgot-link">
                  Forgot Password?
                </Link>
              </div>
            )}
          </form>
        </div>
    </AuthLayout>
  );
};

export default Login;
